import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { rimsList, rimsUpsert, rimsDelete } from "@/lib/api";
import {
  ActiveBadge,
  DeleteConfirm,
  TextField,
  NumberField,
  TextareaField,
  SwitchField,
  slugify,
} from "./RimsKit";

const EMPTY = {
  key: "",
  name: "",
  category: "",
  description: "",
  icon: "",
  order: null,
  active: true,
};

export const IndustriesManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await rimsList(token, "industries")) || []);
    } catch {
      toast.error("Failed to load industries");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openCreate = () => {
    setMode("create");
    setForm({ ...EMPTY });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setMode("edit");
    setForm({ ...EMPTY, ...item });
    setDialogOpen(true);
  };

  const handleToggleActive = async (item) => {
    setTogglingKey(item.key);
    setItems((prev) =>
      prev.map((r) => (r.key === item.key ? { ...r, active: !r.active } : r))
    );
    try {
      await rimsUpsert(token, "industries", item.key, { ...item, active: !item.active });
      toast.success(`${item.name} ${!item.active ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update industry");
      load();
    } finally {
      setTogglingKey("");
    }
  };

  const handleSave = async () => {
    const key = form.key?.trim() || slugify(form.name);
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!key) return toast.error("Key is required");
    if (mode === "create" && items.some((r) => r.key === key)) {
      return toast.error(`Key "${key}" already exists`);
    }
    setSaving(true);
    try {
      const payload = { ...form, key };
      if (payload.order === "") payload.order = null;
      await rimsUpsert(token, "industries", key, payload);
      toast.success(mode === "edit" ? "Industry updated" : "Industry created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save industry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rimsDelete(token, "industries", deleteTarget.key);
      toast.success("Industry deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete industry");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="rims-industries-manager">
      <div className="flex justify-between items-center mb-5">
        <p className="text-white/50 text-sm">
          Industries shown in the assessment. Disabled industries are hidden from applicants.
        </p>
        <Button
          data-testid="rims-add-industry-button"
          onClick={openCreate}
          className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg h-10"
        >
          <Plus size={16} className="mr-1" /> Add Industry
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="rims-industries-empty">
            No industries found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60 w-16">Active</TableHead>
                  <TableHead className="text-white/60">Industry</TableHead>
                  <TableHead className="text-white/60 hidden md:table-cell">Category</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Status</TableHead>
                  <TableHead className="text-white/60 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.key} data-testid="rims-industry-row" className="border-white/10 hover:bg-white/5">
                    <TableCell>
                      <Switch
                        data-testid={`rims-industry-toggle-${r.key}`}
                        checked={!!r.active}
                        disabled={togglingKey === r.key}
                        onCheckedChange={() => handleToggleActive(r)}
                        className="data-[state=checked]:bg-[#2EE59D]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-white">{r.name}</div>
                      <div className="text-white/40 text-xs">{r.key}</div>
                    </TableCell>
                    <TableCell className="text-white/70 hidden md:table-cell">{r.category || "-"}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <ActiveBadge active={r.active} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-edit-industry-${r.key}`}
                          onClick={() => openEdit(r)}
                          className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-delete-industry-${r.key}`}
                          onClick={() => setDeleteTarget(r)}
                          className="h-8 w-8 text-white/60 hover:text-[#FF5C7A] hover:bg-white/10"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{items.length} industr{items.length === 1 ? "y" : "ies"}</p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#0F1424] border-white/10 text-white" data-testid="rims-industry-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {mode === "edit" ? "Edit Industry" : "Add Industry"}
            </DialogTitle>
            <DialogDescription className="text-white/45">
              Industries appear in the assessment. Disabled industries are hidden from applicants.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-1">
            <TextField label="Name *" testid="rims-industry-name-input" value={form.name} onChange={(v) => set({ name: v })} />
            <TextField
              label="Key *"
              testid="rims-industry-key-input"
              value={form.key}
              disabled={mode === "edit"}
              hint={mode === "edit" ? "Key cannot be changed." : "Auto-generated if blank."}
              onChange={(v) => set({ key: slugify(v) })}
            />
            <TextField label="Category" value={form.category} onChange={(v) => set({ category: v })} />
            <NumberField label="Display Order" value={form.order ?? ""} onChange={(v) => set({ order: v === "" ? null : v })} />
          </div>
          <TextareaField label="Description" value={form.description} onChange={(v) => set({ description: v })} />
          <SwitchField
            label="Active (visible in the assessment)"
            testid="rims-industry-active-switch"
            checked={form.active}
            onChange={(v) => set({ active: v })}
          />
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-white/70 hover:text-white hover:bg-white/5">
              Cancel
            </Button>
            <Button
              data-testid="rims-industry-save-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : null}
              {mode === "edit" ? "Save Changes" : "Create Industry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete industry?"
        description={`This will permanently remove "${deleteTarget?.name}" from the assessment industry list.`}
        onConfirm={handleDelete}
        busy={deleting}
        testid="rims-delete-industry-dialog"
      />
    </div>
  );
};
